/**
 * Canonical finding priority (pillar A, OpenSpec findings-triage-queue).
 *
 *   raw   = Impact + Compliance + HumanTest + Reach + Recurrence + Regression + Age
 *   score = min(100, round(raw × Criticality × Confidence))
 *   band  = P1 ≥ 50 · P2 30–49 · P3 15–29 · P4 < 15
 *
 * Pure and deterministic so the CLI can rank identically offline once this
 * moves to `@iamu-dev/protocol`. Every factor carries its points and a
 * one-line reason: the stored breakdown is the explanation.
 */
import { z } from "zod";

export const PRIORITY_BANDS = ["P1", "P2", "P3", "P4"] as const;
export type PriorityBand = (typeof PRIORITY_BANDS)[number];

export const PRIORITY_FACTOR_KEYS = [
  "impact",
  "compliance",
  "human_test",
  "reach",
  "recurrence",
  "regression",
  "age",
] as const;
export type PriorityFactorKey = (typeof PRIORITY_FACTOR_KEYS)[number];

const points = z.number().int().min(0).max(100);
const multiplier = z.number().min(0).max(3);

/**
 * Weights stored before `nit` replaced `observation` name the level
 * `observation`; read them as `nit`.
 */
export function normalizeSeverityWeights<T>(severity: T): T {
  if (!severity || typeof severity !== "object") return severity;
  const { observation, ...rest } = severity as Record<string, unknown>;
  return (
    observation !== undefined && rest.nit === undefined
      ? { ...rest, nit: observation }
      : severity
  ) as T;
}

export const priorityWeightsSchema = z.object({
  severity: z.preprocess(
    normalizeSeverityWeights,
    z.object({
      critical: points,
      major: points,
      minor: points,
      nit: points,
    }),
  ),
  taskImpact: z.object({ blocks: points, impairs: points, slows: points }),
  regulatoryWeight: z.object({
    hard_requirement: points,
    process_model: points,
    best_practice: points,
  }),
  complianceImpact: z.object({
    critical: points,
    major: points,
    minor: points,
  }),
  humanTest: z.object({ no: points, partially: points }),
  reach: z.object({
    twoPersonas: points,
    threePlusPersonas: points,
    twoPlusScenarios: points,
  }),
  recurrence: z.object({
    seenTwice: points,
    seenThreePlus: points,
    persistedAfterFix: points,
  }),
  regression: points,
  age: z.object({
    weeksPerPoint: z.number().int().min(1).max(52),
    max: points,
  }),
  criticality: z.object({
    critical_path: multiplier,
    important: multiplier,
    optional: multiplier,
  }),
  confidence: z.object({
    high: multiplier,
    medium: multiplier,
    low: multiplier,
  }),
});
export type PriorityWeights = z.infer<typeof priorityWeightsSchema>;

export const BALANCED_WEIGHTS: PriorityWeights = {
  severity: { critical: 50, major: 30, minor: 12, nit: 3 },
  taskImpact: { blocks: 50, impairs: 30, slows: 12 },
  // Compliance scoring is disabled (0 pts) until authoritative source backing is integrated.
  regulatoryWeight: {
    hard_requirement: 0,
    process_model: 0,
    best_practice: 0,
  },
  complianceImpact: { critical: 0, major: 0, minor: 0 },
  humanTest: { no: 20, partially: 12 },
  reach: { twoPersonas: 6, threePlusPersonas: 10, twoPlusScenarios: 4 },
  recurrence: { seenTwice: 3, seenThreePlus: 6, persistedAfterFix: 5 },
  regression: 10,
  age: { weeksPerPoint: 2, max: 5 },
  criticality: { critical_path: 1.25, important: 1.0, optional: 0.75 },
  confidence: { high: 1.0, medium: 0.85, low: 0.6 },
};

export const PRIORITY_PRESETS = {
  balanced: { label: "Balanced", weights: BALANCED_WEIGHTS },
  compliance_first: {
    label: "Compliance-first",
    // Regulatory ×2, human test ×1.5.
    weights: {
      ...BALANCED_WEIGHTS,
      regulatoryWeight: {
        hard_requirement: 40,
        process_model: 20,
        best_practice: 8,
      },
      complianceImpact: { critical: 40, major: 24, minor: 8 },
      humanTest: { no: 30, partially: 18 },
    },
  },
  task_first: {
    label: "Task-first",
    // What the persona could not do outweighs how bad it looked.
    weights: {
      ...BALANCED_WEIGHTS,
      severity: { critical: 40, major: 24, minor: 10, nit: 2 },
      taskImpact: { blocks: 60, impairs: 36, slows: 15 },
    },
  },
} as const satisfies Record<
  string,
  { label: string; weights: PriorityWeights }
>;
export type PriorityPreset = keyof typeof PRIORITY_PRESETS | "custom";
export const PRIORITY_PRESET_KEYS = [
  "balanced",
  "compliance_first",
  "task_first",
  "custom",
] as const satisfies readonly PriorityPreset[];

export interface PriorityJudgeCitation {
  rating: "no" | "partially";
  humanTestId: string;
  walkthroughId: string;
  /** The human-test question, when known. */
  question: string | null;
}

/** Everything the score reads about one canonical. */
export interface PriorityInput {
  findingType: string | null;
  /** Worst severity / task impact / compliance values in the current window. */
  severity: string | null;
  taskImpact: string | null;
  regulatoryWeight: string | null;
  complianceImpact: string | null;
  /** Worst judge rating citing a member, latest completed run per scenario. */
  judge: PriorityJudgeCitation | null;
  personaCount: number;
  scenarioCount: number;
  observationCount: number;
  /** A re-run saw it again after a fix was applied. */
  persistedAfterFix: boolean;
  state: string;
  firstSeenAt: Date;
  now: Date;
  /** Highest outline `task_criticality` across its scenarios. */
  criticality: string | null;
  /** Walker confidence; null = not recorded (counts as 1.0). */
  confidence: string | null;
  /** Label of the outline carrying the criticality, for the reason line. */
  criticalityOutline?: string | null;
}

export interface PriorityFactor {
  key: PriorityFactorKey;
  points: number;
  /** One-line reason, e.g. `Major hinders` or `Seen 2×; persisted after a fix`. */
  reason: string;
  detail?: Record<string, unknown>;
}

export interface PriorityMultiplier {
  key: "criticality" | "confidence";
  value: number;
  reason: string;
}

export interface PriorityBreakdown {
  total: number;
  band: PriorityBand;
  weightsVersion: number;
  factors: PriorityFactor[];
  multipliers: PriorityMultiplier[];
}

const PROBLEM_TYPES = new Set(["hinders", "missing", "incorrect"]);

/** Only problem types (hinders / missing / incorrect) are scored. */
export function isScoredType(type: string | null | undefined): boolean {
  return !!type && PROBLEM_TYPES.has(type);
}

/**
 * A problem goes through the full lifecycle (fix → re-run); `helps` and
 * `irrelevant` do not. An unknown type (a canonical with no member yet) is
 * treated as a problem: the stricter rule set never lets praise reach
 * fix_applied by accident, and a problem is never blocked. The one
 * predicate for "offer a fix" / "count as a problem" across the platform.
 */
export function isProblemType(type: string | null | undefined): boolean {
  return type == null || isScoredType(type);
}

export function priorityBand(score: number): PriorityBand {
  if (score >= 50) return "P1";
  if (score >= 30) return "P2";
  if (score >= 15) return "P3";
  return "P4";
}

/** Score range a band covers — used to place an override in the sort. */
export function bandRange(band: PriorityBand): [number, number] {
  switch (band) {
    case "P1":
      return [50, 100];
    case "P2":
      return [30, 49];
    case "P3":
      return [15, 29];
    case "P4":
      return [0, 14];
  }
}

/** Sort key of an overridden canonical: its score clamped into the band. */
export function overrideSortScore(score: number, band: PriorityBand): number {
  const [lo, hi] = bandRange(band);
  return Math.min(hi, Math.max(lo, score));
}

const pick = <T extends Record<string, number>>(
  table: T,
  key: string | null,
): number => (key && key in table ? table[key as keyof T] : 0);

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const human = (s: string) => s.replace(/_/g, " ");

function impactFactor(i: PriorityInput, w: PriorityWeights): PriorityFactor {
  const sev = pick(w.severity, i.severity);
  const task = pick(w.taskImpact, i.taskImpact);
  const byTask = task > sev;
  const reason = byTask
    ? `${cap(human(i.taskImpact ?? ""))} the task`
    : i.severity
      ? `${cap(i.severity)} ${i.findingType ?? "problem"}`
      : "Severity not recorded";
  return {
    key: "impact",
    points: Math.max(sev, task),
    reason,
    detail: { severity: i.severity, taskImpact: i.taskImpact },
  };
}

function complianceFactor(
  i: PriorityInput,
  w: PriorityWeights,
): PriorityFactor {
  const reg = pick(w.regulatoryWeight, i.regulatoryWeight);
  const comp = pick(w.complianceImpact, i.complianceImpact);
  const points = Math.max(reg, comp);
  const reason =
    points === 0
      ? "Compliance scoring disabled (backing pending)"
      : reg >= comp
        ? `Regulatory weight: ${human(i.regulatoryWeight ?? "")}`
        : `${cap(i.complianceImpact ?? "")} compliance impact`;
  return {
    key: "compliance",
    points,
    reason,
    detail: {
      regulatoryWeight: i.regulatoryWeight,
      complianceImpact: i.complianceImpact,
    },
  };
}

function humanTestFactor(i: PriorityInput, w: PriorityWeights): PriorityFactor {
  if (!i.judge) {
    return {
      key: "human_test",
      points: 0,
      reason: "Not cited by the judge",
    };
  }
  const q = i.judge.question ? `: "${i.judge.question}"` : "";
  return {
    key: "human_test",
    points: w.humanTest[i.judge.rating],
    reason: `Cited in a "${i.judge.rating}" verdict${q}`,
    detail: { ...i.judge },
  };
}

function reachFactor(i: PriorityInput, w: PriorityWeights): PriorityFactor {
  const personas =
    i.personaCount >= 3
      ? w.reach.threePlusPersonas
      : i.personaCount === 2
        ? w.reach.twoPersonas
        : 0;
  const scenarios = i.scenarioCount >= 2 ? w.reach.twoPlusScenarios : 0;
  const p = Math.max(i.personaCount, 1);
  const s = Math.max(i.scenarioCount, 1);
  return {
    key: "reach",
    points: personas + scenarios,
    reason: `${p} persona${p === 1 ? "" : "s"} · ${s} scenario${s === 1 ? "" : "s"}`,
    detail: { personaCount: i.personaCount, scenarioCount: i.scenarioCount },
  };
}

function recurrenceFactor(
  i: PriorityInput,
  w: PriorityWeights,
): PriorityFactor {
  const seen =
    i.observationCount >= 3
      ? w.recurrence.seenThreePlus
      : i.observationCount === 2
        ? w.recurrence.seenTwice
        : 0;
  const persisted = i.persistedAfterFix ? w.recurrence.persistedAfterFix : 0;
  const parts = [`Seen ${Math.max(i.observationCount, 1)}×`];
  if (i.persistedAfterFix) parts.push("persisted after a fix");
  return {
    key: "recurrence",
    points: seen + persisted,
    reason: parts.join("; "),
    detail: {
      observationCount: i.observationCount,
      persistedAfterFix: i.persistedAfterFix,
    },
  };
}

function ageFactor(i: PriorityInput, w: PriorityWeights): PriorityFactor {
  const diffMs =
    !i.firstSeenAt || Number.isNaN(i.firstSeenAt.getTime())
      ? 0
      : Math.max(0, i.now.getTime() - i.firstSeenAt.getTime());
  const weeks = diffMs / (7 * 86_400_000);
  const pts = Math.min(w.age.max, Math.floor(weeks / w.age.weeksPerPoint));
  const whole = Math.floor(weeks);
  return {
    key: "age",
    points: pts,
    reason:
      whole < 1
        ? "First seen this week"
        : `First seen ${whole} week${whole === 1 ? "" : "s"} ago`,
    detail: { weeks: whole },
  };
}

/**
 * Score one canonical. Returns null for `helps` / `irrelevant` (they sit
 * outside the queue). Unknown confidence counts as 1.0 — missing data is
 * not penalised.
 */
export function computeCanonicalPriority(
  input: PriorityInput,
  weights: PriorityWeights = BALANCED_WEIGHTS,
  weightsVersion = 1,
): PriorityBreakdown | null {
  if (!isScoredType(input.findingType)) return null;
  const regressed = input.state === "regressed";
  const factors: PriorityFactor[] = [
    impactFactor(input, weights),
    complianceFactor(input, weights),
    humanTestFactor(input, weights),
    reachFactor(input, weights),
    recurrenceFactor(input, weights),
    {
      key: "regression",
      points: regressed ? weights.regression : 0,
      reason: regressed ? "Came back after it was verified" : "Not regressed",
    },
    ageFactor(input, weights),
  ];
  const crit =
    input.criticality && input.criticality in weights.criticality
      ? weights.criticality[
          input.criticality as keyof PriorityWeights["criticality"]
        ]
      : weights.criticality.important;
  const conf =
    input.confidence && input.confidence in weights.confidence
      ? weights.confidence[
          input.confidence as keyof PriorityWeights["confidence"]
        ]
      : 1.0;
  const multipliers: PriorityMultiplier[] = [
    {
      key: "criticality",
      value: crit,
      reason: input.criticality
        ? `${input.criticalityOutline ? `${input.criticalityOutline}: ` : ""}${human(input.criticality)}`
        : "Not recorded",
    },
    {
      key: "confidence",
      value: conf,
      reason: input.confidence ? `${cap(input.confidence)}` : "Not recorded",
    },
  ];
  const total = scoreFromBreakdown(factors, multipliers);
  return {
    total,
    band: priorityBand(total),
    weightsVersion,
    factors,
    multipliers,
  };
}

/** Sum × multipliers, rounded, capped at 100 — the displayed score. */
export function scoreFromBreakdown(
  factors: readonly Pick<PriorityFactor, "points">[],
  multipliers: readonly Pick<PriorityMultiplier, "value">[],
): number {
  const raw = factors.reduce((s, f) => s + f.points, 0);
  const mult = multipliers.reduce((m, x) => m * x.value, 1);
  // Round to 6 decimals first so 24 × 1.25 × 0.85 doesn't drift.
  return Math.min(100, Math.round(Number((raw * mult).toFixed(6))));
}

/** The "worse" snapshot an override is judged against. */
export interface PriorityBasis {
  severityRank: number;
  taskImpactRank: number;
  personaCount: number;
  scenarioCount: number;
  judgeNo: boolean;
}

/**
 * Has the canonical got worse since the override was set (CONTRACT §1
 * "worse": severity or task impact rose, a new persona or scenario hit it,
 * or a judge `no` verdict cites it)?
 */
export function isWorseThanBasis(
  before: PriorityBasis,
  now: PriorityBasis,
): boolean {
  return (
    now.severityRank > before.severityRank ||
    now.taskImpactRank > before.taskImpactRank ||
    now.personaCount > before.personaCount ||
    now.scenarioCount > before.scenarioCount ||
    (now.judgeNo && !before.judgeNo)
  );
}
